import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/components/auth/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, MessageSquare, Filter, User, Brain, Settings, Copy, Hash, MessagesSquare } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FormattedMessage } from '@/components/ui/formatted-message';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface ThinkingMethod {
  id: string;
  name: string;
  author: string;
  bio: string;
  icon_url: string | null;
  order_index: number;
}

interface Profile {
  is_admin: boolean;
  name: string;
  email: string;
}

interface Conversation {
  id: string;
  user_id: string;
  thinking_method_id: string;
  created_at: string;
  updated_at: string;
  thinking_method: ThinkingMethod;
  profile: Profile;
  message_count: number;
}

interface MessageProperties {
  total_cost_cents?: number;
  indications?: {
    final_stage?: string;
    is_artifact_question?: boolean;
    is_complete_answer?: boolean;
    scope_rejected?: boolean;
    clarification_asked?: boolean;
    safety_blocked?: boolean;
    safety_reason_code?: string;
    reformulated?: boolean;
  };
}

interface Message {
  id: string;
  conversation_id: string;
  content: string;
  is_user: boolean;
  created_at: string;
  kind?: string;
  thinking_method_id?: string | null;
  properties?: MessageProperties;
}

const PropertiesBadges = ({
  properties,
  tone = 'default',
}: {
  properties?: MessageProperties;
  tone?: 'default' | 'inverse';
}) => {
  if (!properties || typeof properties !== 'object') return null;
  const ind = properties.indications || {};
  const costCents = properties.total_cost_cents;

  const badgeTone: Record<'cost' | 'stage' | 'warning' | 'success' | 'danger' | 'accent', string> = tone === 'inverse'
    ? {
        cost: 'border-white/15 bg-white/15 text-primary-foreground',
        stage: 'border-white/15 bg-white/15 text-primary-foreground',
        warning: 'border-white/15 bg-white/15 text-primary-foreground',
        success: 'border-white/15 bg-white/15 text-primary-foreground',
        danger: 'border-white/15 bg-white/15 text-primary-foreground',
        accent: 'border-white/15 bg-white/15 text-primary-foreground',
      }
    : {
        cost: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
        stage: 'border border-sky-200 bg-sky-50 text-sky-700',
        warning: 'border border-amber-200 bg-amber-50 text-amber-700',
        success: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
        danger: 'border border-red-200 bg-red-50 text-red-700',
        accent: 'border border-violet-200 bg-violet-50 text-violet-700',
      };

  const indicators: { label: string; color: string }[] = [];

  if (ind.final_stage) indicators.push({ label: `stage: ${ind.final_stage}`, color: badgeTone.stage });
  if (ind.is_artifact_question) indicators.push({ label: 'artifact_q', color: badgeTone.warning });
  if (ind.is_complete_answer) indicators.push({ label: 'complete', color: badgeTone.success });
  if (ind.scope_rejected) indicators.push({ label: 'scope_rejected', color: badgeTone.danger });
  if (ind.clarification_asked) indicators.push({ label: 'clarification', color: badgeTone.warning });
  if (ind.safety_blocked) indicators.push({ label: `safety: ${ind.safety_reason_code || 'blocked'}`, color: badgeTone.danger });
  if (ind.reformulated) indicators.push({ label: 'reformulated', color: badgeTone.accent });

  if (indicators.length === 0 && (costCents === null || costCents === undefined)) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {costCents != null && (
        <Badge className={`text-[10px] px-2 py-0.5 h-5 font-mono font-bold tracking-wider ${badgeTone.cost}`}>
          ${(Number(costCents) / 100).toFixed(3)}
        </Badge>
      )}
      {indicators.map((ind, i) => (
        <Badge key={i} className={`text-[10px] px-2 py-0.5 h-5 font-mono font-bold tracking-wider border-0 ${ind.color}`}>
          {ind.label}
        </Badge>
      ))}
    </div>
  );
};

const CollapsibleMessage = ({ message, thinkingMethods }: { message: Message; thinkingMethods: ThinkingMethod[] }) => {
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();
  const isLong = message.content.split('\n').length > 2 || message.content.length > 200;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied`, description: text.slice(0, 60) + (text.length > 60 ? '…' : '') });
  };

  return (
    <div className={`flex ${message.is_user ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-2xl px-5 py-4 rounded-2xl shadow-sm relative group ${
          message.is_user
            ? 'bg-primary text-primary-foreground ml-12'
            : 'bg-card text-foreground mr-12 border border-border/70'
        }`}
      >
        <div
          className={`${!expanded && isLong ? 'line-clamp-2' : ''} cursor-pointer`}
          onClick={() => isLong && setExpanded(!expanded)}
        >
          <FormattedMessage
            className="prose-sm"
            content={message.content}
            tone={message.is_user ? 'inverse' : 'default'}
          />
        </div>
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`text-xs font-medium mt-1 ${
              message.is_user ? 'text-primary-foreground/80 hover:text-primary-foreground' : 'text-secondary-foreground/80 hover:text-foreground'
            }`}
          >
            {expanded ? '▲ Collapse' : '▼ Show more'}
          </button>
        )}
        <PropertiesBadges properties={message.properties} tone={message.is_user ? 'inverse' : 'default'} />
        <div className={`text-xs mt-3 pt-2 border-t border-opacity-20 flex items-center justify-between gap-2 ${
          message.is_user
            ? 'border-primary-foreground/25 text-primary-foreground/80'
            : 'border-border/40 text-secondary-foreground/80'
        }`}>
          <span>{new Date(message.created_at).toLocaleString()}</span>
          <div className="flex items-center gap-1.5">
            {!message.is_user && message.thinking_method_id && (() => {
              const tm = thinkingMethods.find(t => t.id === message.thinking_method_id);
              const label = tm?.name || message.thinking_method_id.slice(0, 8);
              return (
                <Badge
                  className="text-[10px] px-2 py-0.5 h-5 font-mono font-bold tracking-wider border border-indigo-200 bg-indigo-50 text-indigo-700"
                  title={tm ? `${tm.name}${tm.author ? ` · ${tm.author}` : ''}` : message.thinking_method_id}
                >
                  {label}
                </Badge>
              );
            })()}
            {message.kind && (
              <Badge className={`text-[10px] px-2 py-0.5 h-5 font-mono font-bold tracking-wider border-0 ${
                message.is_user ? 'bg-white/15 text-primary-foreground' :
                message.kind === 'CHAT' ? 'border border-sky-200 bg-sky-50 text-sky-700' :
                message.kind === 'ARTF' ? 'border border-emerald-200 bg-emerald-50 text-emerald-700' :
                message.kind === 'QUESTION' ? 'border border-amber-200 bg-amber-50 text-amber-700' :
                message.kind === 'ANSWER' ? 'border border-violet-200 bg-violet-50 text-violet-700' :
                message.kind === 'EDIT' ? 'border border-rose-200 bg-rose-50 text-rose-700' :
                'bg-muted text-muted-foreground'
              }`}>
                {message.kind}
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`p-1 rounded-md transition-colors ${
                  message.is_user
                    ? 'hover:bg-primary-foreground/20 text-primary-foreground/70 hover:text-primary-foreground'
                    : 'hover:bg-muted text-secondary-foreground/80 hover:text-foreground'
                }`}>
                  <Settings className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => copyToClipboard(message.id, 'Message ID')}>
                  <Hash className="h-3.5 w-3.5 mr-2" />
                  Copy message ID
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => copyToClipboard(message.content, 'Message')}>
                  <Copy className="h-3.5 w-3.5 mr-2" />
                  Copy message
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => copyToClipboard(message.conversation_id, 'Conversation ID')}>
                  <MessagesSquare className="h-3.5 w-3.5 mr-2" />
                  Copy conversation ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConversationManager = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const sourceMessageRef = useRef<HTMLDivElement | null>(null);
  const requestedConversationId = searchParams.get('conversationId');
  const requestedMessageId = searchParams.get('messageId');
  const fromApplications = ['applications', 'operating-systems'].includes(searchParams.get('from') ?? '')
    || (!!requestedConversationId && !!requestedMessageId);
  const adminReturnPath = fromApplications ? '/admin?tab=apps' : '/admin';
  const adminReturnLabel = fromApplications ? 'Back to Apps' : 'Back to Admin Panel';
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [thinkingMethods, setThinkingMethods] = useState<ThinkingMethod[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filters, setFilters] = useState({
    thinkingMethodId: 'all',
    userId: 'all',
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    checkAdminStatus();
  }, [user, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchThinkingMethods();
      fetchConversations();
    }
  }, [isAdmin, filters]);

  const checkAdminStatus = async () => {
    if (!user) return;
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('user_id', user.id)
      .single();

    if (!profile?.is_admin) {
      navigate('/');
      toast({
        title: "Access Denied",
        description: "You don't have admin privileges",
        variant: "destructive",
      });
      return;
    }
    setIsAdmin(true);
  };

  const fetchThinkingMethods = async () => {
    const { data, error } = await supabase
      .from('thinking_methods')
      .select('*')
      .order('order_index');

    if (error) {
      toast({
        title: "Error",
        description: "Failed to fetch experts",
        variant: "destructive",
      });
    } else {
      setThinkingMethods(data || []);
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    
    // Get conversations metadata from conversations table (contains user_id and thinking_method_id)
    let conversationsQuery = supabase
      .from('conversations')
      .select(`
        *,
        thinking_methods(
          id, name, author, bio, icon_url, order_index, created_at, updated_at
        )
      `)
      .order('updated_at', { ascending: false });

    if (filters.thinkingMethodId && filters.thinkingMethodId !== 'all') {
      conversationsQuery = conversationsQuery.eq('thinking_method_id', filters.thinkingMethodId);
    }
    if (filters.userId && filters.userId !== 'all') {
      conversationsQuery = conversationsQuery.eq('user_id', filters.userId);
    }

    const { data: conversationsData, error: conversationsError } = await conversationsQuery;

    if (conversationsError) {
      toast({
        title: "Error",
        description: "Failed to fetch conversations",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Get unique user IDs
    const userIds = [...new Set(conversationsData?.map(conv => conv.user_id) || [])];
    
    // Fetch user profiles
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('user_id, name, email, is_admin')
      .in('user_id', userIds);

    // Create a map of user profiles
    const profilesMap = new Map();
    profilesData?.forEach(profile => {
      profilesMap.set(profile.user_id, profile);
    });

    // Combine conversations with profiles and message counts from messages_vr
    const conversationsWithCount = await Promise.all(
      (conversationsData || []).map(async (conv) => {
        const { count } = await supabase
          .from('messages_vr')
          .select('*', { count: 'exact', head: true })
          .eq('conversation_id', conv.id);
        
        const profile = profilesMap.get(conv.user_id) || { 
          name: 'Unknown User', 
          email: 'unknown@example.com', 
          is_admin: false 
        };
        
        return {
          id: conv.id,
          user_id: conv.user_id,
          thinking_method_id: conv.thinking_method_id,
          created_at: conv.created_at,
          updated_at: conv.updated_at,
          thinking_method: conv.thinking_methods as ThinkingMethod,
          profile: profile as Profile,
          message_count: count || 0
        } as Conversation;
      })
    );
    
    setConversations(conversationsWithCount);
    setLoading(false);
  };

  const fetchMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true);
    
    const { data, error } = await supabase
      .from('messages_vr')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to fetch messages",
        variant: "destructive",
      });
    } else {
      setMessages((data || []).map(row => ({ ...row, properties: row.properties && typeof row.properties === "object" && !Array.isArray(row.properties) ? row.properties as MessageProperties : undefined })));
    }
    setMessagesLoading(false);
  }, [toast]);

  const handleConversationSelect = useCallback((conversation: Conversation) => {
    setSelectedConversation(conversation);
    void fetchMessages(conversation.id);
  }, [fetchMessages]);

  useEffect(() => {
    if (!requestedConversationId || loading || selectedConversation?.id === requestedConversationId) return;

    const requestedConversation = conversations.find((conversation) => conversation.id === requestedConversationId);
    if (requestedConversation) {
      handleConversationSelect(requestedConversation);
    }
  }, [conversations, handleConversationSelect, loading, requestedConversationId, selectedConversation?.id]);

  useEffect(() => {
    if (!requestedMessageId || messagesLoading || !sourceMessageRef.current) return;
    sourceMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [messages, messagesLoading, requestedMessageId]);

  const resetConversationView = () => {
    setSelectedConversation(null);
    setMessages([]);
    if (requestedConversationId || requestedMessageId) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('conversationId');
      nextSearchParams.delete('messageId');
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  // Get unique users for filtering
  const uniqueUsers = conversations.reduce((acc, conv) => {
    if (!acc.find(user => user.id === conv.user_id)) {
      acc.push({
        id: conv.user_id,
        name: conv.profile.name,
        email: conv.profile.email
      });
    }
    return acc;
  }, [] as Array<{ id: string; name: string | null; email: string }>);

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate(adminReturnPath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {adminReturnLabel}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Conversation Manager</h1>
            <p className="text-muted-foreground">View and manage all user conversations</p>
          </div>
        </div>

        {selectedConversation ? (
          <div className="flex flex-col h-[calc(100vh-120px)] space-y-6">
            <div className="flex items-center gap-4 p-6 bg-card rounded-lg border">
              <Button variant="outline" onClick={resetConversationView} className="shrink-0">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Conversations
              </Button>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-foreground truncate">
                  {selectedConversation.thinking_method.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedConversation.profile.name} • {selectedConversation.profile.email}
                </p>
              </div>
              <Badge variant="secondary" className="shrink-0">
                {messages.length} messages
              </Badge>
            </div>

            <Card className="flex-1 overflow-hidden">
              <CardContent className="p-0 h-full">
                <ScrollArea className="h-[calc(100vh-300px)] p-6">
                  {messagesLoading ? (
                    <div className="flex justify-center items-center p-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {messages.map((message) => {
                        const isSourceMessage = message.id === requestedMessageId;
                        return (
                          <div
                            key={message.id}
                            ref={isSourceMessage ? sourceMessageRef : undefined}
                            className={isSourceMessage ? 'rounded-2xl ring-2 ring-primary/50 ring-offset-4 ring-offset-background' : undefined}
                          >
                            <CollapsibleMessage message={message} thinkingMethods={thinkingMethods} />
                          </div>
                        );
                      })}
                      {messages.length === 0 && (
                        <div className="text-center text-muted-foreground py-12">
                          <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No messages in this conversation yet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Filters */}
            <Card className="border-2 border-muted/50">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Filter className="h-5 w-5 text-primary" />
                  </div>
                  Filters
                </CardTitle>
                <CardDescription>
                  Filter conversations by expert lens or user
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Brain className="h-4 w-4 text-primary" />
                      Filter by Method
                    </label>
                    <Select value={filters.thinkingMethodId} onValueChange={(value) => setFilters(prev => ({ ...prev, thinkingMethodId: value }))}>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="All Methods" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Methods</SelectItem>
                        {thinkingMethods.map((method) => (
                          <SelectItem key={method.id} value={method.id}>
                            {method.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <User className="h-4 w-4 text-primary" />
                        Filter by User
                      </label>
                      {filters.userId !== 'all' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setFilters(prev => ({ ...prev, userId: 'all' }))}
                          className="text-xs text-muted-foreground hover:text-foreground h-auto px-2 py-1"
                        >
                          Clear filter
                        </Button>
                      )}
                    </div>
                    <Select value={filters.userId} onValueChange={(value) => setFilters(prev => ({ ...prev, userId: value }))}>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="All Users" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {uniqueUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Conversations Display */}
            {loading ? (
              <div className="flex justify-center items-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {conversations.length === 0 ? (
                  <Card className="border-2 border-dashed border-muted">
                    <CardContent className="p-12 text-center">
                      <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="text-lg font-semibold mb-2">No conversations found</h3>
                      <p className="text-muted-foreground">No conversations match the current filters.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-2 border-muted/50 overflow-hidden">
                    <CardHeader className="border-b bg-muted/30 pb-4">
                      <CardTitle className="flex items-center gap-3 text-lg">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <MessageSquare className="h-5 w-5 text-primary" />
                        </div>
                        Conversations
                        <Badge variant="secondary" className="ml-auto">
                          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                       <div className="divide-y divide-border">
                         {conversations.map((conversation: Conversation, index) => (
                           <div
                             key={conversation.id}
                             className="p-6 cursor-pointer hover:bg-muted/30 transition-all duration-200 group"
                             onClick={() => handleConversationSelect(conversation)}
                           >
                             <div className="flex items-start justify-between">
                               <div className="flex-1 min-w-0 space-y-3">
                                 <div className="flex items-center gap-3">
                                   <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                                     <Brain className="h-4 w-4 text-primary" />
                                   </div>
                                   <div className="min-w-0 flex-1">
                                     <h3 className="font-semibold text-foreground truncate text-base group-hover:text-primary transition-colors">
                                       {conversation.thinking_method.name}
                                     </h3>
                                     <p className="text-sm text-muted-foreground">
                                       by {conversation.thinking_method.author}
                                     </p>
                                   </div>
                                 </div>
                                 
                                 <div className="flex items-center gap-3 pl-14">
                                   <div className="p-1.5 bg-muted rounded-md">
                                     <User className="h-3.5 w-3.5 text-muted-foreground" />
                                   </div>
                                   <div className="min-w-0 flex-1">
                                     <div className="font-medium text-sm text-foreground">{conversation.profile.name}</div>
                                     <div className="text-xs text-muted-foreground truncate">
                                       {conversation.profile.email}
                                     </div>
                                   </div>
                                 </div>
                               </div>
                               
                               <div className="flex flex-col items-end gap-3 ml-6 shrink-0">
                                 <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1">
                                   <MessageSquare className="h-3 w-3" />
                                   <span className="font-medium">{conversation.message_count}</span>
                                 </Badge>
                                 <div className="text-xs text-muted-foreground font-medium">
                                   {new Date(conversation.updated_at).toLocaleDateString()}
                                 </div>
                               </div>
                             </div>
                           </div>
                         ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationManager;
